import React from 'react';
import type { FC } from 'react';
import kebabCase from 'lodash/kebabCase.js';
import { TextInput, SelectInput } from '@wix/cli-ui-kit';
import { getEmbeddedScriptExtensionsDir } from '@wix/cli-astro-definitions';
import {
  EmbedCategory,
  EmbeddedScriptPlacement,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { useWizardStep } from '../Wizard.js';
import {
  createEmbeddedScriptFolderValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import { useWixCliAstroBi } from '../../../bi/index.js';
import { useProjectModel } from '../../../model.js';

export const ChooseScriptNameStep: FC<{
  scriptName?: string;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, scriptName }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <TextInput
      isActive={isActive}
      label={'Script name:'}
      placeholder={scriptName ?? 'My Script'}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'Script name:',
          questionKey: 'generate_command.embedded_script.choose_name',
          answer: value,
          additionalInfo: { extensionType: 'embedded script' },
        });
        onSubmit(value);
        goToNextStep();
      }}
    />
  );
};

export const ChooseScriptFolderStep: FC<{
  scriptName?: string;
  scriptFolder?: string;
  onSubmit: (scriptFolder: string) => void;
}> = ({ onSubmit, scriptFolder, scriptName }) => {
  const { model } = useProjectModel();
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();
  const defaultScriptFolder = kebabCase(scriptName);
  const validateEmbeddedScriptFolder = useValidation(
    createEmbeddedScriptFolderValidation(
      getEmbeddedScriptExtensionsDir(model.projectFolder)
    )
  );

  return (
    <TextInput
      isActive={isActive}
      label={'Enter the folder for the new script:'}
      placeholder={scriptFolder ?? defaultScriptFolder}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'Enter the folder for the new script:',
          questionKey: 'generate_command.dashboard_page.choose_route',
          answer: value,
          additionalInfo: { extensionType: 'embedded script' },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateEmbeddedScriptFolder}
    />
  );
};

export const ChooseScriptTypeStep: FC<{
  onSubmit: (value: EmbedCategory) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();

  const scripTypes = [
    {
      title: 'Essential',
      description: 'Must load regardless of policy',
      value: EmbedCategory.ESSENTIAL,
    },
    {
      title: 'Functional',
      description: 'Adds optional functionality to the site',
      value: EmbedCategory.FUNCTIONAL,
    },
    {
      title: 'Analytics',
      description: 'Adds analytics abilities to the site',
      value: EmbedCategory.ANALYTICS,
    },
    {
      title: 'Advertising',
      description:
        'Adds advertising content or advertising tracking to the site',
      value: EmbedCategory.ADVERTISING,
    },
  ];

  return (
    <SelectInput<EmbedCategory>
      isActive={isActive}
      label={'Script type:'}
      options={scripTypes}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: 'Script type:',
          questionKey: 'generate_command.embedded_script.choose_type',
          answer: option.title,
          additionalInfo: { extensionType: 'embedded script' },
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};

export const ChooseScriptPlacementStep: FC<{
  onSubmit: (value: EmbeddedScriptPlacement) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();

  const scriptPlacements = [
    {
      title: 'Head',
      description: 'In the document head',
      value: EmbeddedScriptPlacement.HEAD,
    },
    {
      title: 'Body Start',
      description: 'Prepended in the body',
      value: EmbeddedScriptPlacement.BODY_START,
    },
    {
      title: 'Body End',
      description: 'Appended in the body',
      value: EmbeddedScriptPlacement.BODY_END,
    },
  ];

  return (
    <SelectInput<EmbeddedScriptPlacement>
      isActive={isActive}
      label={'Script placement:'}
      options={scriptPlacements}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: 'Script placement:',
          questionKey: 'generate_command.embedded_script.choose_placement',
          answer: option.title,
          additionalInfo: { extensionType: 'embedded script' },
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};
